import { leyanbotClient, ImResponse, resolve } from 'services/api';

interface StatsGlobalSummaryResponse {
  /**
   * 截止当日已启用总数
   */
  totalEnabledCount: number;
  /**
   * 截止当日已提交总数
   */
  totalSubmittedCount: number;
  /**
   * 自学习回复的问题数（过去7天平均）
   */
  avgAiLearningReplyCount: number;
}

/**
 * 获取全局统计
 */
export const getStatsGlobalSummary = (signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.get<ImResponse<StatsGlobalSummaryResponse>>(
      '/oversea-knowledge/supplemental_knowledge/stats/global_summary',
      { signal },
    ),
  );
};

interface StatsChartData<T> {
  xAxis: string[];
  series: Array<{
    name: T;
    data: number[];
  }>;
}

type StatsWeeklyTrendResponse = StatsChartData<'提交数' | '启用数' | '自学习回复数'>;

/**
 * 获取周趋势
 */
export const getStatsWeeklyTrend = (signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.get<ImResponse<StatsWeeklyTrendResponse>>(
      '/oversea-knowledge/supplemental_knowledge/stats/weekly_trend',
      { signal },
    ),
  );
};

export interface StatsQueryAgentBody {
  /**
   * 部门ID
   */
  departmentIds?: number[];
  /**
   * 提交人ID
   */
  userIds?: number[];
  /**
   * 平台店铺范围
   */
  platform?: string;
  /**
   * 店铺类型
   */
  storeType?: string;
  /**
   * 店铺ID列表
   */
  storeIds?: number[];
  /**
   * 开始日期
   */
  startDate: string;
  /**
   * 结束日期
   */
  endDate: string;
}

export interface StatQueryAgent {
  label: string;
  currentValue: number;
  comparisonStartDate: string;
  comparisonEndDate: string;
  growthRate: number;
  extraDesc: string;
}

export interface StatsQueryAgentResponse {
  /**
   * 提交知识数指标
   */
  submittedStats: StatQueryAgent;
  /**
   * 启用知识数指标
   */
  enabledStats: StatQueryAgent;
  /**
   * 自学习回答问题数指标
   */
  aiLearningReplyStats: StatQueryAgent;
  /**
   * 启用知识数趋势（按天维度）
   */
  knowledgeEnabledTrend: StatsChartData<'提交数' | '启用数' | '启用率'>;
  /**
   * 自学习回答问题数趋势
   */
  aiLearningReplyTrend: StatsChartData<'自学习回复问题数'>;
  /**
   * 自学习提示可吸收问题数
   */
  aiLearningPromptCount: number;
}

/**
 * 查询客服统计信息
 */
export const getStatsQueryAgent = (body: StatsQueryAgentBody, signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.post<ImResponse<StatsQueryAgentResponse>>(
      '/oversea-knowledge/supplemental_knowledge/stats/query_agent',
      body,
      { signal },
    ),
  );
};

export interface StatsAgentRankBody extends StatsQueryAgentBody {
  pageNo: number;
  pageSize: number;
}

interface StatsSubmitRank {
  agentId: number;
  agentName: string;
  submittedCount: number;
  enabledCount: number;
  aiLearningReplyCount: number;
  aiLearningPromptCount: number;
  correctionCount: number;
  enabledRate: number;
  submittedRate: number;
}

/**
 * 获取客服提交排行榜
 */
export const getStatsAgentSubmitRank = (body: StatsAgentRankBody, signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.post<ImResponse<{ ranks: StatsSubmitRank[]; total: number }>>(
      '/oversea-knowledge/supplemental_knowledge/stats/agent_submit_rank',
      body,
      { signal },
    ),
  );
};

interface StatsReviewRank {
  agentId: number;
  agentName: string;
  reviewedCount: number;
  passedCount: number;
  passedRate: number;
  avgReviewDuration: number;
}

/**
 * 获取客服审核排行榜
 */
export const getStatsAgentReviewRank = (body: StatsAgentRankBody, signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.post<ImResponse<{ ranks: StatsReviewRank[]; total: number }>>(
      '/oversea-knowledge/supplemental_knowledge/stats/agent_review_rank',
      body,
      { signal },
    ),
  );
};

interface StatsAiReplyConversionResponse {
  conversations: Array<{
    conversationId: number;
    questionMsgId: number;
  }>;
  total: number;
}

/**
 * 获取AI回复的会话明细
 */
export const getStatsAiReplyConversion = (body: StatsAgentRankBody, signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.post<ImResponse<StatsAiReplyConversionResponse>>(
      '/oversea-knowledge/supplemental_knowledge/stats/ai_reply_conversion',
      body,
      { signal },
    ),
  );
};

export interface ExportStatsAgentRankBody extends StatsQueryAgentBody {
  language: General.SystemLanguage;
  exportTypes: ('AGENT_SUBMITTED_DETAIL' | 'AGENT_REVIEWED_DETAIL')[];
}

export const exportStatsAgentRank = (body: ExportStatsAgentRankBody) => {
  return resolve(
    leyanbotClient.post<ImResponse<string>>(
      '/oversea-knowledge/supplemental_knowledge/stats/export',
      body,
    ),
  );
};
